import * as React from 'react';

export interface ErrorBoundaryProps {
  children:
    | ((hasError: boolean, error: Error | null) => JSX.Element)
    | JSX.Element;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.PureComponent<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {hasError: true, error};
  }
  public readonly state: Readonly<ErrorBoundaryState> = {
    hasError: false,
    error: null,
  };

  public render(): React.ReactNode {
    const {children} = this.props;
    const {hasError, error} = this.state;

    return typeof children === 'function'
      ? children(hasError, error)
      : children;
  }
}

export const withErrorBoundary = <P, R>(
  Component: React.ComponentType<P>
): React.ForwardRefExoticComponent<
  React.PropsWithoutRef<P> & React.RefAttributes<R>
> =>
  React.forwardRef<R, P>((props, ref) => {
    const {...restProps} = props as React.PropsWithoutRef<P>;

    return (
      <ErrorBoundary>
        {(hasError) =>
          !hasError ? (
            <Component {...(restProps as P)} ref={ref} />
          ) : (
            <span>
              An error occurred while rendering component{' '}
              {Component.displayName}
            </span>
          )
        }
      </ErrorBoundary>
    );
  });
